
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CreditCard, MessageCircle, Zap, Check, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const PixPaymentModal = () => {
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    // Verificar se deve mostrar o modal
    const shouldShowModal = () => {
      if (!user) return false;
      
      // Não mostrar para usuários assinantes
      if (user.isSubscribed) return false;
      
      // Mostrar apenas para usuários gratuitos ou em teste
      if (user.plan === 'free' || !user.plan) {
        return true;
      }
      
      return false;
    };

    // Verificar se já foi mostrado hoje
    const lastShown = localStorage.getItem('pixModalLastShown');
    const today = new Date().toDateString();
    
    if (shouldShowModal() && lastShown !== today) {
      // Mostrar após 3 segundos
      const timer = setTimeout(() => {
        setShowModal(true);
        localStorage.setItem('pixModalLastShown', today);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [user]);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      "Olá! Gostaria de adquirir o acesso completo da plataforma através do PIX. Pode me ajudar com os detalhes do pagamento?"
    );
    window.open(`https://wa.me/5573988489821?text=${message}`, "_blank");
    setShowModal(false);
  };

  const handleClose = () => {
    setShowModal(false);
  };

  if (!showModal) return null;

  return (
    <Dialog open={showModal} onOpenChange={setShowModal}>
      <DialogContent className="max-w-md mx-auto">
        <DialogHeader className="text-center">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <DialogTitle className="text-xl font-bold text-green-600 mb-2">
                🚀 Acesso Completo Disponível!
              </DialogTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
          <CardContent className="p-6 text-center">
            <div className="mb-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CreditCard className="h-8 w-8 text-green-600" />
              </div>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Não tem Cartão de Crédito?
              </h3>
              
              <p className="text-gray-600 text-sm mb-4">
                Sem problemas! Você pode adquirir o acesso completo através do <strong>PIX</strong>
              </p>
            </div>

            <div className="bg-white rounded-lg p-4 mb-4 border border-green-200">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Zap className="h-5 w-5 text-green-600" />
                <span className="font-semibold text-green-700">Vantagens do PIX</span>
              </div>
              
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                  <span>Ativação imediata após pagamento</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                  <span>Sem necessidade de cartão</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                  <span>Processo rápido e seguro</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                  <span>Suporte personalizado</span>
                </div>
              </div>
            </div>

            <Button
              onClick={handleWhatsAppClick}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2"
            >
              <MessageCircle className="h-5 w-5" />
              Falar no WhatsApp sobre PIX
            </Button>
            
            <p className="text-xs text-gray-500 mt-3">
              Respondemos rapidamente no WhatsApp!
            </p>
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
};

export default PixPaymentModal;
